import { keepPreviousData, QueryKey, useQuery } from "@tanstack/react-query";
import moment from "moment";
import "moment/locale/ru";

/** Корень ключей react-query для всех виджетов дашборда — по нему работает кнопка «Обновить». */
export const DASHBOARD_QUERY_KEY = "dashboard";

/**
 * Общий запрос для виджетов дашборда:
 * - кэш на 5 минут — при переключении периода туда-обратно данные не грузятся заново;
 * - при смене периода показываем прошлые данные, пока грузятся новые (без «мигания» скелетонов).
 */
export function useDashboardQuery<T>(key: QueryKey, queryFn: () => Promise<T>) {
  return useQuery<T>({
    queryKey: [DASHBOARD_QUERY_KEY, ...(key as unknown[])],
    queryFn,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    placeholderData: keepPreviousData,
  });
}

const numberFormat = new Intl.NumberFormat("ru-RU");
const compactFormat = new Intl.NumberFormat("ru-RU", { notation: "compact", maximumFractionDigits: 1 });

/** 1234567 → «1 234 567» */
export const formatNumber = (value: unknown) => numberFormat.format(Number(value) || 0);

/** 1234567 → «1,2 млн» — для осей графиков. */
export const formatCompact = (value: unknown) => compactFormat.format(Number(value) || 0);

/** Доля в процентах с одним знаком: 12.3 */
export const toPercent = (value: number, total: number) => (total > 0 ? Math.round((value / total) * 1000) / 10 : 0);

/**
 * Месяцы (YYYY-MM), попадающие в последние `months` месяцев, включая текущий.
 * Период «за месяц» (4 окт – 4 ноя) захватывает два календарных месяца,
 * поэтому ключей на один больше, чем `months`.
 */
export function getMonthKeys(months: number): string[] {
  const keys: string[] = [];
  const cursor = moment().subtract(months, "month").startOf("month");
  const end = moment().startOf("month");
  while (cursor.isSameOrBefore(end)) {
    keys.push(cursor.format("YYYY-MM"));
    cursor.add(1, "month");
  }
  return keys;
}

/** «2026-11» → «ноя 26» */
export const formatMonthKey = (key: string) => moment(key, "YYYY-MM").locale("ru").format("MMM YY").replace(".", "");

/** Человекочитаемый диапазон для выбранного периода: «4 нояб. 2025 — 4 нояб. 2026». */
export function formatPeriodRange(months: number) {
  const from = moment().subtract(months, "month").locale("ru");
  const to = moment().locale("ru");
  return `${from.format("D MMM YYYY")} — ${to.format("D MMM YYYY")}`;
}
